// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Checkbox, Container, Group, PasswordInput, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconUser, IconMail, IconLock, IconArrowLeft, IconPhoto } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { AuthIllustration, SocialLoginButtons, PasswordStrength } from '../components';

export default function RegisterIllustrated() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password });
  };

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left side - Illustration & Image */}
      <Box
        style={{
          width: '50%',
          display: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
        visibleFrom="md"
      >
        {/* Stock image background */}
        <BackgroundImage
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />

        {/* Overlay */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${theme.colors.teal[9]}dd, ${theme.colors.green[9]}cc)`,
          }}
        />

        {/* Content */}
        <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <SlideIn direction="down" delay={200}>
              <AuthIllustration type="register" size={250} animated />
            </SlideIn>

            <FadeIn delay={400}>
              <Stack align="center" gap="sm">
                <Title order={2} c="white" ta="center">
                  Join Our Community
                </Title>
                <Text c="white" ta="center" opacity={0.9} maw={350}>
                  Create your account and start building amazing things with our platform.
                </Text>
              </Stack>
            </FadeIn>

            {/* Benefits */}
            <FadeIn delay={600}>
              <Stack gap="sm" mt="lg">
                {[
                  'Free tier with generous limits',
                  'Access to all core features',
                  '24/7 community support',
                ].map((benefit, i) => (
                  <Group key={i} gap="xs">
                    <Text c="white">✓</Text>
                    <Text c="white" opacity={0.9} size="sm">{benefit}</Text>
                  </Group>
                ))}
              </Stack>
            </FadeIn>
          </Stack>
        </Center>
      </Box>

      {/* Right side - Form */}
      <Box
        style={{
          flex: 1,
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
          overflowY: 'auto',
        }}
      >
        <Center mih="100vh" p="xl">
          <Container size="xs" w="100%">
            <Stack gap="lg">
              {/* Back button */}
              <Button
                component={Link}
                to="/demo/auth-pages/register"
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                w="fit-content"
              >
                Back to variants
              </Button>

              {/* Header */}
              <Stack align="center" gap="xs">
                <Badge
                  leftSection={<IconPhoto size={12} />}
                  variant="gradient"
                  gradient={{ from: 'teal', to: 'green' }}
                  size="lg"
                >
                  Illustrated Design
                </Badge>
                <Title order={2} ta="center" mt="md">
                  Create an account
                </Title>
                <Text c="dimmed" size="sm" ta="center">
                  Get started with your free account
                </Text>
              </Stack>

              {/* Mobile illustration */}
              <Center hiddenFrom="md">
                <AuthIllustration type="register" size={120} animated />
              </Center>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <Stack gap="md">
                  <TextInput
                    label="Full name"
                    placeholder="John Doe"
                    leftSection={<IconUser size={16} />}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    size="md"
                    radius="md"
                  />

                  <TextInput
                    label="Email"
                    placeholder="your@email.com"
                    leftSection={<IconMail size={16} />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    size="md"
                    radius="md"
                  />

                  <div>
                    <PasswordInput
                      label="Password"
                      placeholder="Create a password"
                      leftSection={<IconLock size={16} />}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      size="md"
                      radius="md"
                    />
                    {password && <PasswordStrength password={password} variant="bar" />}
                  </div>

                  <PasswordInput
                    label="Confirm password"
                    placeholder="Confirm your password"
                    leftSection={<IconLock size={16} />}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    size="md"
                    radius="md"
                    error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : undefined}
                  />

                  <Checkbox
                    label={
                      <Text size="sm">
                        I agree to the <Anchor size="sm">Terms</Anchor> and{' '}
                        <Anchor size="sm">Privacy Policy</Anchor>
                      </Text>
                    }
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    radius="md"
                    variant="gradient"
                    gradient={{ from: 'teal', to: 'green' }}
                    disabled={!acceptTerms}
                  >
                    Create account
                  </Button>
                </Stack>
              </form>

              {/* Social Login */}
              <SocialLoginButtons
                providers={['google', 'github', 'apple']}
                layout="icons-only"
                dividerLabel="or sign up with"
              />

              {/* Login Link */}
              <Text ta="center" size="sm">
                Already have an account?{' '}
                <Anchor component={Link} to="/demo/auth-pages/login/illustrated" fw={500}>
                  Sign in
                </Anchor>
              </Text>
            </Stack>
          </Container>
        </Center>
      </Box>
    </Box>
  );
}
